const MAX_FIFO_QUEUE_SIZE = 50

type CacheSpaceType = {
  [key: string | number]: unknown
}
type CacheType = {
  [key: number]: CacheSpaceType
}
const ClientSideGlobalFifoCacheStorage: CacheType = {}
let ClientSideGlobalCacheSpaceIndex = 0
const FifoStorage: Array<{ space: number, key: string | number }> = []

export type Appender = <T>(p: { key: string | number, space: number, value: T }) => void
const AppendGlobalCache: Appender = ({ key, space, value }) => {
  ClientSideGlobalFifoCacheStorage[space] = ClientSideGlobalFifoCacheStorage[space] || {}
  ClientSideGlobalFifoCacheStorage[space][key] = value
  FifoStorage.push({ space, key })
  if (FifoStorage.length > MAX_FIFO_QUEUE_SIZE) {
    const removable = FifoStorage.shift()
    if (removable && !(removable.space === space && removable.key === key)) {
      delete ClientSideGlobalFifoCacheStorage[removable.space][removable.key]
    }
  }
}

const GlobalCacheContains = ({ key, space }) => (
  !!Object.getOwnPropertyDescriptor(ClientSideGlobalFifoCacheStorage, space)
  && !!Object.getOwnPropertyDescriptor(ClientSideGlobalFifoCacheStorage[space], key)
)

const GlobalCacheAccess = <T>({ key, space }) => (
  ClientSideGlobalFifoCacheStorage?.[space]?.[key]
) as T

export const ClientSideCache = <T>(computation: (...args: T[]) => number | string) => (
  <K>(functor: (...args: T[]) => Promise<K>) => {
    if (!process.browser) {
      return functor
    }
    const space = ClientSideGlobalCacheSpaceIndex
    ClientSideGlobalCacheSpaceIndex += 1
    return async (...args) => {
      const key = computation(...args)
      const value = (
        GlobalCacheContains({ space, key })
          ? GlobalCacheAccess<K>({ space, key })
          : await functor(...args)
      )
      AppendGlobalCache({ space, key, value })
      return GlobalCacheAccess<K>({ key, space })
    }
  }
)
